/** @jsxImportSource ./simboluri-jsx */
import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { FemModel } from "./tipuri";

// Viewer 3D pentru deformate (forme modale / săgeți din greutate):
// structura nedeformată = wireframe gri, deformata = linii colorate după
// mărimea deplasării (albastru = mic, roșu = mare). `field` = [ux, uy, uz]
// per nod, aliniat cu model.noduri (id = index + 1); `scale` înmulțește
// deplasările; `animate` oscilează deformata sinusoidal (pentru moduri).
// La varianta shell, mesh-ul plăcii (model.shells) se desenează ca un set
// de segmente deformate — modurile locale de placă devin vizibile.

export default function Deformata3D({
  model,
  field,
  scale,
  animate,
  height = 460,
}: {
  model: FemModel;
  field: number[][];
  scale: number;
  animate: boolean;
  height?: number;
}) {
  const mount = useRef<HTMLDivElement>(null);
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const api = useRef<{ set: (f: number) => void; render: () => void } | null>(null);

  useEffect(() => {
    const el = mount.current;
    if (!el || !field.length) return;

    const idx = new Map(model.noduri.map((n, i) => [n.id, i]));
    const P = model.noduri.map((n) => [n.x_m, n.y_m, n.z_m]);
    const xs = P.map((p) => p[0]);
    const ys = P.map((p) => p[1]);
    const zs = P.map((p) => p[2]);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    const cz = (Math.min(...zs) + Math.max(...zs)) / 2;
    const span = Math.max(
      Math.max(...xs) - Math.min(...xs),
      Math.max(...ys) - Math.min(...ys),
      Math.max(...zs) - Math.min(...zs)
    );

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf4f6fa);

    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, span * 20);
    camera.up.set(0, 0, 1);
    camera.position.set(cx + span * 0.9, cy - span * 1.1, cz + span * 0.7);

    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    el.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(cx, cy, cz);
    controls.update();

    // lanțurile de noduri: grinzile prin nodurile lor FEM, stâlpii cap-cap
    const lanturi: number[][] = [];
    model.grinzi.forEach((g) => lanturi.push(g.noduri.map((id) => idx.get(id)!)));
    model.stalpi.forEach((s) =>
      lanturi.push([idx.get(s.nod_jos)!, idx.get(s.nod_sus)!])
    );

    // wireframe-ul nedeformat (gri)
    const matGri = new THREE.LineBasicMaterial({ color: 0xb9c2d0 });
    lanturi.forEach((ln) => {
      const pts = ln.map((i) => new THREE.Vector3(P[i][0], P[i][1], P[i][2]));
      scene.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), matGri));
    });

    // deformata: o geometrie per lanț, actualizată la fiecare cadru/scal
    const umax = Math.max(...field.map((u) => Math.hypot(u[0], u[1], u[2])), 1e-12);
    const matDef = new THREE.LineBasicMaterial({ vertexColors: true, linewidth: 2 });
    const deformate: { geo: THREE.BufferGeometry; ln: number[] }[] = [];
    lanturi.forEach((ln) => {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(ln.length * 3), 3));
      const colors = new Float32Array(ln.length * 3);
      ln.forEach((i, k) => {
        const u = field[i] ?? [0, 0, 0];
        const m = Math.hypot(u[0], u[1], u[2]) / umax; // 0..1
        const c = new THREE.Color().setHSL(0.62 * (1 - m), 0.85, 0.45);
        colors[3 * k] = c.r;
        colors[3 * k + 1] = c.g;
        colors[3 * k + 2] = c.b;
      });
      geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
      const line = new THREE.Line(geo, matDef);
      // fără frustum culling nu mai trebuie recalculată sfera de încadrare la
      // fiecare cadru (era O(noduri) × O(lanțuri) × 60/s — sursa de sacadare
      // la varianta shell, cu mii de muchii)
      line.frustumCulled = false;
      scene.add(line);
      deformate.push({ geo, ln });
    });

    // mesh-ul plăcii (varianta shell): muchiile unice ale quad-urilor,
    // ca un singur LineSegments (mii de muchii — o singură geometrie)
    let meshGeo: THREE.BufferGeometry | null = null;
    let meshIdx: number[] = [];
    if (model.shells?.length) {
      const edges = new Set<string>();
      model.shells.forEach((sh) => {
        const q = sh.noduri;
        for (let k = 0; k < 4; k++) {
          const a = q[k];
          const b = q[(k + 1) % 4];
          edges.add(a < b ? `${a},${b}` : `${b},${a}`);
        }
      });
      meshIdx = [];
      edges.forEach((e) => {
        const [a, b] = e.split(",").map(Number);
        meshIdx.push(idx.get(a)!, idx.get(b)!);
      });
      meshGeo = new THREE.BufferGeometry();
      meshGeo.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(meshIdx.length * 3), 3)
      );
      const colors = new Float32Array(meshIdx.length * 3);
      meshIdx.forEach((i, k) => {
        const u = field[i] ?? [0, 0, 0];
        const m = Math.hypot(u[0], u[1], u[2]) / umax;
        const c = new THREE.Color().setHSL(0.62 * (1 - m), 0.8, 0.55);
        colors[3 * k] = c.r;
        colors[3 * k + 1] = c.g;
        colors[3 * k + 2] = c.b;
      });
      meshGeo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
      const seg = new THREE.LineSegments(
        meshGeo,
        new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.55 })
      );
      seg.frustumCulled = false;
      scene.add(seg);
    }

    const setDeformata = (factor: number) => {
      deformate.forEach(({ geo, ln }) => {
        const pos = geo.getAttribute("position") as THREE.BufferAttribute;
        ln.forEach((i, k) => {
          const u = field[i] ?? [0, 0, 0];
          pos.setXYZ(
            k,
            P[i][0] + u[0] * factor,
            P[i][1] + u[1] * factor,
            P[i][2] + u[2] * factor
          );
        });
        pos.needsUpdate = true;
      });
      if (meshGeo) {
        const pos = meshGeo.getAttribute("position") as THREE.BufferAttribute;
        meshIdx.forEach((i, k) => {
          const u = field[i] ?? [0, 0, 0];
          pos.setXYZ(
            k,
            P[i][0] + u[0] * factor,
            P[i][1] + u[1] * factor,
            P[i][2] + u[2] * factor
          );
        });
        pos.needsUpdate = true;
      }
    };

    const grid = new THREE.GridHelper(
      Math.ceil(span * 1.4), Math.ceil(span * 1.4), 0xd5dce8, 0xe8edf5);
    grid.rotation.x = Math.PI / 2;
    grid.position.set(cx, cy, -0.02);
    scene.add(grid);

    const render = () => renderer.render(scene, camera);
    controls.addEventListener("change", render);
    api.current = { set: setDeformata, render };

    let raf = 0;
    let alive = true;
    // întâi poziția statică (amplitudine maximă) — prima redare e corectă și
    // dacă rAF nu rulează încă (tab ascuns); animația preia apoi controlul
    setDeformata(scaleRef.current);
    if (animate) {
      const t0 = performance.now();
      const loop = () => {
        if (!alive) return;
        const t = (performance.now() - t0) / 1000;
        setDeformata(scaleRef.current * Math.sin(2 * Math.PI * t / 2.5));
        render();
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    const resize = () => {
      const w = el.clientWidth || 900;
      const h = el.clientHeight || height;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      render();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    window.addEventListener("resize", resize);
    resize();

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      api.current = null;
      ro.disconnect();
      window.removeEventListener("resize", resize);
      controls.dispose();
      renderer.dispose();
      el.removeChild(renderer.domElement);
      scene.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.geometry) m.geometry.dispose();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, field, animate]);

  // schimbarea scalei fără animație: actualizează deformata pe loc
  // (cu animație, bucla rAF citește oricum scaleRef la fiecare cadru)
  useEffect(() => {
    if (!animate && api.current) {
      api.current.set(scale);
      api.current.render();
    }
  }, [scale, animate]);

  return <div ref={mount} className="fem-canvas" style={{ height }} />;
}
